/**
 * A spring balance, spliced into a line to read what it is carrying.
 *
 * It has NO physics of its own, and that is the point of building it. It
 * applies no force, adds no row and carries no mass; what it shows is a
 * multiplier the engine already had. A part that can be added without teaching
 * the solver anything is the clearest evidence there is that the multipliers
 * are the right primitive — and it is the cheapest possible test of that
 * claim, which is why the target doc lists it at all.
 *
 * ## Why the rope above and the rope below are two ropes
 *
 * Because they have to be able to differ, and here they cannot. A balance in a
 * line is a massless body with one degree of freedom, so the row that says
 * `ΣF = 0` on it is the row that says the tension above equals the tension
 * below — which is the reading, and is exactly the fact a student is being
 * asked to notice. Threading one rope straight through would have asserted
 * that equality instead of deriving it, and would have needed a new kind of
 * path stop to say so.
 */
import { JOINS, numbered, PartKind } from '../registry';
import { ACROSS, DOWN } from '../vec';
import { Part, Port, RopeContact, Vec2 } from '../types';

export interface SpringBalanceData { [k: string]: unknown }

/** Half its height: the dial, and a hook at each end. */
export const BALANCE_HALF = 0.22;

export const springBalance: PartKind<SpringBalanceData> = {
    key: 'springBalance',
    label: 'Spring balance',
    home: 1.4,
    create: (at, id): Part<SpringBalanceData> => ({ id, kind: 'springBalance', at, data: {} }),
    ports: (part): Port[] => [
        { id: 'top', partId: part.id, role: 'anchorage', joins: JOINS.one, at: { x: part.at.x, y: part.at.y + BALANCE_HALF } },
        { id: 'bottom', partId: part.id, role: 'anchorage', joins: JOINS.one, at: { x: part.at.x, y: part.at.y - BALANCE_HALF } },
    ],
    /** Ideal, so that what it reads is what was there before it was added. A
     *  balance heavy enough to change the answer is a balance nobody would use. */
    mass: () => 0,
    extent: (): Vec2 => ({ x: 0.13, y: BALANCE_HALF }),
    /* Two freedoms — see `weight.freedom`. */
    freedom: () => ({ axes: [ACROSS, DOWN] }),
    bondable: true,
    ropeContact: (part, port): RopeContact => ({ at: port.at }),
    tag: 'G',
    /** Nowhere. It is drawn as a graduated scale with its reading beside it,
     *  and a scale is what it looks like: a label saying "the balance" next to
     *  one was the drawing explaining itself. The name is still what the panels
     *  and the free-body diagrams call it. */
    nameAt: () => null,
    name: (part, w) => numbered(part, w, 'G'),
};
